// frontend/src/pages/admin/AdminPromotionsPage.js
import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Award,
  Plus,
  Edit,
  Trash2,
  Search,
  RefreshCw,
  X,
  Store,
  Upload,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight
} from 'lucide-react';
import useAdminStore from '../../store/adminStore';
import useAuthStore from '../../store/authStore';
import axiosInstance from '../../utils/axiosConfig';

const AdminPromotionsPage = () => {
  const { user } = useAuthStore();
  const {
    promotions,
    promotionsPagination,
    isLoading,
    getAllPromotions,
    createPromotion,
    updatePromotion,
    deletePromotion,
  } = useAdminStore();

  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    isActive: '',
    search: '',
  });

  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [formData, setFormData] = useState({
    businessId: '',
    title: '',
    description: '',
    imageUrl: '',
    rotationInterval: 'ONE_MINUTE',
    startDate: '',
    endDate: '',
    priority: 0,
  });

  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  const ROTATION_OPTIONS = [
    { value: 'ONE_MINUTE', label: '1 Minute' },
    { value: 'TWO_MINUTES', label: '2 Minutes' },
    { value: 'THREE_MINUTES', label: '3 Minutes' },
    { value: 'FIVE_MINUTES', label: '5 Minutes' },
    { value: 'TEN_MINUTES', label: '10 Minutes' },
    { value: 'FIFTEEN_MINUTES', label: '15 Minutes' },
    { value: 'THIRTY_MINUTES', label: '30 Minutes' },
    { value: 'SIXTY_MINUTES', label: '60 Minutes' },
  ];

  useEffect(() => {
    loadPromotions();
  }, [filters]);

  const loadPromotions = async () => {
    try {
      const { page, limit, ...rest } = filters;
      const cleanFilters = {};
      if (rest.isActive) cleanFilters.isActive = rest.isActive;
      if (rest.search) cleanFilters.search = rest.search;
      
      await getAllPromotions({ ...cleanFilters, page: page || 1, limit: limit || 10 });
    } catch (error) {
      console.error('Failed to load promotions:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      businessId: '',
      title: '',
      description: '',
      imageUrl: '',
      rotationInterval: 'ONE_MINUTE',
      startDate: '',
      endDate: '',
      priority: 0,
    });
    setImagePreview('');
    setEditingPromotion(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (promotion) => {
    setEditingPromotion(promotion);
    setFormData({
      businessId: promotion.businessId,
      title: promotion.title,
      description: promotion.description || '',
      imageUrl: promotion.imageUrl || '',
      rotationInterval: promotion.rotationInterval || 'ONE_MINUTE',
      startDate: promotion.startDate ? promotion.startDate.split('T')[0] : '',
      endDate: promotion.endDate ? promotion.endDate.split('T')[0] : '',
      priority: promotion.priority || 0,
    });
    if (promotion.imageUrl) {
      setImagePreview(promotion.imageUrl);
    }
    setShowModal(true);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, WEBP, GIF)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);

    try {
      const response = await axiosInstance.post('/marketplace/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.success && response.data?.data?.url) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.data.url }));
        toast.success('Image uploaded successfully!');
      } else if (response.data?.url) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.url }));
        toast.success('Image uploaded successfully!');
      } else {
        toast.error(response.data?.error?.message || 'Failed to upload image');
        setImagePreview('');
      }
    } catch (error) {
      console.error('Image upload failed:', error);
      let errorMessage = 'Failed to upload image. Please try again.';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      setImagePreview('');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.businessId || !formData.title) {
      toast.error('Business ID and title are required');
      return;
    }

    try {
      if (editingPromotion) {
        await updatePromotion(editingPromotion.id, formData);
        toast.success('Promotion updated successfully!');
      } else {
        await createPromotion(formData);
        toast.success('Promotion created successfully!');
      }
      setShowModal(false);
      resetForm();
      await loadPromotions();
    } catch (error) {
      console.error('Failed to save promotion:', error);
      let errorMessage = 'Failed to save promotion';
      if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        await deletePromotion(id);
        toast.success('Promotion deleted successfully!');
        await loadPromotions();
      } catch (error) {
        console.error('Failed to delete promotion:', error);
        let errorMessage = 'Failed to delete promotion';
        if (error.response?.data?.error?.message) {
          errorMessage = error.response.data.error.message;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 10,
      isActive: '',
      search: '',
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (promotionsPagination?.totalPages || 1)) {
      setFilters({ ...filters, page: newPage });
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setFilters({ ...filters, limit: newLimit, page: 1 });
  };

  const getRotationLabel = (interval) => {
    const option = ROTATION_OPTIONS.find(opt => opt.value === interval);
    return option ? option.label : interval || '1 Minute';
  };

  const total = promotionsPagination?.total || 0;
  const currentPage = promotionsPagination?.page || 1;
  const totalPages = promotionsPagination?.totalPages || 1;
  const limit = promotionsPagination?.limit || 10;
  const startIndex = (currentPage - 1) * limit + 1;
  const endIndex = Math.min(currentPage * limit, total);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-neomorph p-3 border border-primary-100/30">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center">
            <div className="w-1 h-5 bg-gradient-to-b from-indigo-400 to-indigo-600 rounded-full mr-2" />
            <div>
              <h3 className="text-base font-semibold text-gray-800">Business Promotions</h3>
              <p className="text-xs text-gray-500">Manage rotating business promotions</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-8 pr-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs w-36"
              />
            </div>
            <select
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value })}
              className="px-2 py-1.5 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset border-0 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              onClick={clearFilters}
              className="px-2.5 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs"
            >
              Clear
            </button>
            <button
              onClick={openCreateModal}
              className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-medium rounded-lg shadow-md shadow-indigo-500/30 hover:shadow-lg transition-all flex items-center text-xs"
            >
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Add
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 text-primary-500 animate-spin" />
        </div>
      ) : promotions && promotions.length > 0 ? (
        <>
          <div className="bg-white rounded-xl shadow-neomorph border border-primary-100/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-primary-100/50 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Business</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Rotation</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Clicks</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Last Shown</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-primary-100/50">
                  {promotions.map((promotion) => (
                    <tr key={promotion.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <div className="flex items-center">
                          {promotion.business?.logoUrl ? (
                            <img src={promotion.business.logoUrl} alt="" className="h-8 w-8 rounded-lg object-cover mr-2" />
                          ) : (
                            <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center mr-2">
                              <Store className="h-4 w-4 text-indigo-600" />
                            </div>
                          )}
                          <div>
                            <div className="text-xs font-medium text-gray-800">{promotion.business?.name || 'Unknown'}</div>
                            <div className="text-[10px] text-gray-500">{promotion.business?.category || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs text-gray-800">{promotion.title}</div>
                        {promotion.description && (
                          <div className="text-[10px] text-gray-500 truncate max-w-xs">{promotion.description}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{getRotationLabel(promotion.rotationInterval)}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                          promotion.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                          {promotion.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{promotion.priority || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{promotion.views || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">{promotion.clicks || 0}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-[10px] text-gray-500">
                        {promotion.lastShownAt ? new Date(promotion.lastShownAt).toLocaleString() : 'Never'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="flex items-center space-x-1.5">
                          <button onClick={() => openEditModal(promotion)} className="p-1.5 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all" title="Edit">
                            <Edit className="h-3.5 w-3.5 text-blue-600" />
                          </button>
                          <button onClick={() => handleDelete(promotion.id)} className="p-1.5 bg-red-50 rounded-lg hover:bg-red-100 transition-all" title="Delete">
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {promotionsPagination && promotionsPagination.total > 0 && (
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 bg-white rounded-xl p-3 shadow-neomorph border border-primary-100/30">
              <div className="flex items-center space-x-2">
                <label className="text-xs text-gray-600">Show:</label>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="px-1.5 py-1 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs text-gray-500">per page</span>
              </div>

              <span className="text-xs text-gray-500">
                Showing <span className="font-medium text-gray-800">{startIndex}</span> to{' '}
                <span className="font-medium text-gray-800">{endIndex}</span> of{' '}
                <span className="font-medium text-gray-800">{total}</span> promotions
              </span>

              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1 || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === 1 || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="First"
                >
                  <ChevronsLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === 1 || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Previous"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>

                <div className="flex items-center space-x-0.5">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30'
                            : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === totalPages || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Next"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages || isLoading}
                  className={`p-1.5 rounded-lg transition-all ${
                    currentPage === totalPages || isLoading
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-600 hover:bg-white hover:shadow-neomorph-hover'
                  }`}
                  title="Last"
                >
                  <ChevronsRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-neomorph p-8 text-center border border-primary-100/30">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 shadow-neomorph-inset mb-3">
            <Award className="h-8 w-8 text-indigo-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 mb-1">No Promotions Found</h3>
          <p className="text-sm text-gray-500 mb-3">
            {filters.search || filters.isActive
              ? 'Try adjusting your filters.'
              : 'Promote businesses to help them get more visibility.'}
          </p>
          {(filters.search || filters.isActive) && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-all inline-flex items-center mr-2 text-xs"
            >
              Clear Filters
            </button>
          )}
          <button
            onClick={openCreateModal}
            className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-medium rounded-lg shadow-md shadow-indigo-500/30 hover:shadow-lg transition-all inline-flex items-center text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" /> Add
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-600/50 backdrop-blur-sm" onClick={() => { setShowModal(false); resetForm(); }}></div>

            <div className="inline-block align-bottom bg-[#E8ECF5] rounded-xl shadow-neomorph px-4 pt-4 pb-4 text-left overflow-hidden transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-5 border border-primary-100/30">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-base font-semibold text-gray-800">
                  {editingPromotion ? 'Edit Promotion' : 'Add Promotion'}
                </h3>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="p-1.5 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover">
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </div>

              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Business ID *</label>
                  <input
                    type="text"
                    value={formData.businessId}
                    onChange={(e) => setFormData(prev => ({ ...prev, businessId: e.target.value.trim() }))}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="e.g., 8e8ff9cc-4414-4d81-9db8-3bddc2d593eb"
                  />
                  <p className="text-[10px] text-gray-500 mt-0.5">Find ID in <strong>Businesses</strong> tab</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="e.g., Special Offer at Tech Solutions"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows="2"
                    className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    placeholder="Brief description of the promotion"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Image (Optional)</label>
                  <div className="flex items-start gap-3">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="h-20 w-20 object-cover rounded-lg border-2 border-primary-200 shadow-neomorph-inset" />
                        <button
                          type="button"
                          onClick={() => { setImagePreview(''); setFormData(prev => ({ ...prev, imageUrl: '' })); }}
                          className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 rounded-full text-white hover:bg-red-600 shadow-md transition-all"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                        {formData.imageUrl && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[8px] text-center py-0.5 rounded-b-lg">Uploaded</div>
                        )}
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <div className={`flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed rounded-lg transition-all ${
                          uploadingImage ? 'border-primary-300 bg-primary-50' : 'border-gray-300 hover:border-primary-500 hover:bg-primary-50'
                        }`}>
                          {uploadingImage ? (
                            <>
                              <RefreshCw className="h-5 w-5 text-primary-500 animate-spin" />
                              <span className="text-[10px] text-gray-500 mt-0.5">Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="h-6 w-6 text-gray-400" />
                              <span className="text-[10px] text-gray-400 mt-0.5">Upload</span>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              handleImageUpload(e.target.files[0]);
                            }
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                    <div className="flex-1">
                      <ul className="text-[10px] text-gray-400 space-y-0.5">
                        <li>• JPG, PNG, WEBP, GIF</li>
                        <li>• Max: 5MB</li>
                        <li>• 1200 x 600px recommended</li>
                      </ul>
                      {formData.imageUrl && (
                        <p className="text-[10px] text-green-600 mt-1 truncate">✓ Uploaded</p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Rotation</label>
                    <select
                      value={formData.rotationInterval}
                      onChange={(e) => setFormData(prev => ({ ...prev, rotationInterval: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    >
                      {ROTATION_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 0 }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                      placeholder="0-100"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Start Date</label>
                    <input
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">End Date</label>
                    <input
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset border border-primary-100/30 focus:outline-none focus:ring-2 focus:ring-primary-500/20 text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-4 flex justify-end space-x-2">
                <button onClick={() => { setShowModal(false); resetForm(); }} className="px-3 py-1.5 bg-white text-gray-700 font-medium rounded-lg shadow-neomorph hover:shadow-neomorph-hover text-xs">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !formData.businessId || !formData.title}
                  className="px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white font-medium rounded-lg shadow-md shadow-indigo-500/30 hover:shadow-lg transition-all disabled:opacity-50 text-xs"
                >
                  {isLoading ? 'Saving...' : editingPromotion ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromotionsPage;